import './Projects.css';
import { useState, useEffect, useRef, useMemo } from 'react';
import { ImageSlider } from '../Resources/elements/ImageSlider'
import { PageLine } from '../Resources/elements/pageline'
import { Popup } from '../Resources/elements/Popup'
import { getImageUrl } from '../Resources/firebase/firebase-verify'
import { createPortal } from 'react-dom';

const VIEW_KEY = 'portfolio-project-view'

export function Page({ title, children }) {
    return (
        <div className="page">
            {title &&
                <div>
                    <h2 className="hidden title">{title}</h2>
             
                </div>
            }
            <div className="container">
                {children}
            </div>
        </div>
    )
}

function sortProjects(data) {
    if (!data?.data) return []
    return [...data.data].sort((a, b) => {
        if (!isNaN(a.data.index) && !isNaN(b.data.index)) {
            return a.data.index - b.data.index
        }
        return 1
    })
}

export function ProjectPage({ title, data }) {
    const [view, setView] = useState(() => {
        try {
            return localStorage.getItem(VIEW_KEY) === 'list' ? 'list' : 'cards'
        } catch {
            return 'cards'
        }
    })
    const [open, setOpen] = useState(null)
    const projects = useMemo(() => sortProjects(data), [data])

    function changeView(next) {
        setView(next)
        try { localStorage.setItem(VIEW_KEY, next) } catch { /* private mode */ }
    }

    return (
        <Page title={title}>
            <div className="project-toolbar">
                <p className="project-count">
                    {projects.length} {projects.length === 1 ? 'productie' : 'producties'}
                </p>
                <div className="view-toggle" role="group" aria-label="Weergave">
                    <button
                        type="button"
                        className={view === 'cards' ? 'active' : ''}
                        aria-pressed={view === 'cards'}
                        onClick={() => changeView('cards')}
                    >
                        <GridIcon />
                        Raster
                    </button>
                    <button
                        type="button"
                        className={view === 'list' ? 'active' : ''}
                        aria-pressed={view === 'list'}
                        onClick={() => changeView('list')}
                    >
                        <ListIcon />
                        Lijst
                    </button>
                </div>
            </div>

            {view === 'list' ? (
                <ProjectList projects={projects} onOpen={setOpen} />
            ) : (
                <div className="project-container">
                    {projects.map((d) => (
                        <ProjectCard
                            key={d.data.id}
                            data={d}
                            auto={!open}
                            onOpen={() => setOpen(d)}
                        />
                    ))}
                </div>
            )}

            {open && (
                <ProjectDetail data={open} onClose={() => setOpen(null)} />
            )}
        </Page>
    )
}

function ProjectList({ projects, onOpen }) {
    return (
        <div className="project-list" role="list">
            {projects.map((d, i) => (
                <ProjectRow key={d.data.id || i} data={d} onOpen={() => onOpen(d)} />
            ))}
        </div>
    )
}

function ProjectRow({ data, onOpen }) {
    const material = useMemo(() => (data.get('Materiaal') || []).filter((e) => e.length > 0), [data])
    const format = data.get('Formaat')
    const fotos = data.get('Fotos') || []
    const thumb = fotos.length > 0 ? getImageUrl(fotos[0].data.id, 'small') : null
    const blurb = excerpt(data.get('Beschrijving'))

    return (
        <button type="button" className="project-row" role="listitem" onClick={onOpen}>
            <span className="row-year">{data.get('Jaar')}</span>
            <span className="row-thumb" aria-hidden="true">
                {thumb
                    ? <img src={thumb} alt="" />
                    : <span className="row-thumb-empty">{initials(data.get('Titel'))}</span>
                }
            </span>
            <span className="row-copy">
                <span className="row-title">{data.get('Titel')}</span>
                {data.get('Producent') && <span className="row-producer">{data.get('Producent')}</span>}
                {blurb && <span className="row-blurb">{blurb}</span>}
            </span>
            <span className="row-meta">
                {format && <span>{format}</span>}
                {material.length > 0 && <span>{material.join(', ')}</span>}
            </span>
            <span className="row-open" aria-hidden="true">Bekijk</span>
        </button>
    )
}

function ProjectCard({ data, auto, onOpen }) {
    const elemRef = useRef()
    const isVisible = useIsVisible(elemRef)
    const [load, setLoad] = useState(false)

    return (
        <div className={`project-outer ${load ? 'loaded' : 'hidden'}`}>
            <a href="" className="project" ref={elemRef} onClick={(e) => { onOpen(); e.preventDefault() }}>
                <div className="front">
                    <div className="content">
                        {isVisible && (
                            <div>
                                <ImageSlider
                                    load={setLoad}
                                    shadow={false}
                                    auto={auto}
                                    type="fade"
                                    size="medium"
                                    fit="cover"
                                    id={data.data.id}
                                    key={"img" + data.data.id}
                                    data={data.get('Fotos')}
                                />
                            </div>
                        )}
                        <div className="card-text">
                            <div className="card-title">
                                <h3 className="c-title">{data.get('Titel')}</h3>
                                <div className="year"><span style={{ margin: '0px 2px' }}>|</span>{data.get('Jaar')}</div>
                            </div>
                            <div className="card-mention">
                                {data.get('Producent')}
                            </div>
                        </div>
                    </div>
                </div>
                <Background />
            </a>
        </div>
    )
}

function ProjectDetail({ data, onClose }) {
    const material = (data.get('Materiaal') || []).filter((e) => e.length > 0)

    return createPortal(
        <Popup
            title={data.get('Titel')}
            size="flex"
            Background={Background}
            onClose={onClose}
            content={(
                <div className="project-expand">
                    <div>
                        <div className="grid">
                            <div>
                                <ImageSlider
                                    shadow={true}
                                    allowFullscreen
                                    type="slide"
                                    size="large"
                                    fit="contain"
                                    id={data.id}
                                    key={"img" + data.id}
                                    data={data.get('Fotos')}
                                    control={true}
                                    captions={true}
                                    list={true}
                                />
                                <PageLine padding="8px" width="85%" size="1px" />
                            </div>
                            <div className="details">
                                <div className="main">
                                    <div className="producent">{data.get('Producent')}</div>
                                    <div className="year">{data.get('Jaar')}</div>
                                </div>
                                <div className="meta">
                                    <div className="grid2">
                                        {data.get('Formaat')?.length > 0 && (
                                            <div className="format"><strong>Formaat:</strong><p>{data.get('Formaat')}</p></div>
                                        )}
                                        {material.length > 0 && (
                                            <div className="material"><strong>Materiaal:</strong><p>{material.join(', ')}</p></div>
                                        )}
                                    </div>
                                </div>
                                <div className="credits">
                                    {(data.get('Groep') || []).map((g, i) => (
                                        <div key={i}>
                                            <div></div>
                                            <div>
                                                <strong>{g.label}: </strong>
                                                {g.value.map((p, j) => {
                                                    const end = (j === g.value.length - 1) ? '.' : ','
                                                    return <span key={j} style={{ fontStyle: 'italic' }}>{p}{end} </span>
                                                })}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <div className="description">{data.get('Beschrijving')}</div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        />,
        document.body
    )
}

export function Background() {
    const outer = {
      /*  backgroundColor: 'rgb(138 138 138 / 13%)',*/
        position: 'absolute',
        zIndex: '-10',
        height: '100%',
        width: '100%',
        top: '0px',
    }
    const inner = {
        background: 'url(`../../public/images/lacer.png`)',
        backgroundSize: '50%',
        height: '100%',
        width: '100%',
        filter: 'brightness(50%) sepia(100) saturate(1) hue-rotate(190deg)',
        opacity: '0.080',
    }

    return (
        <div style={outer}>
            <div style={inner} />
        </div>
    )
}

function excerpt(text, max = 140) {
    if (!text) return ''
    const clean = String(text).replace(/\s+/g, ' ').trim()
    if (clean.length <= max) return clean
    return clean.slice(0, max).replace(/\s+\S*$/, '') + '…'
}

function initials(title) {
    if (!title) return '·'
    return String(title).split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase()
}

function GridIcon() {
    return (
        <svg viewBox="0 0 16 16" aria-hidden="true">
            <rect x="1" y="1" width="6" height="6" />
            <rect x="9" y="1" width="6" height="6" />
            <rect x="1" y="9" width="6" height="6" />
            <rect x="9" y="9" width="6" height="6" />
        </svg>
    )
}

function ListIcon() {
    return (
        <svg viewBox="0 0 16 16" aria-hidden="true">
            <rect x="1" y="2" width="14" height="2" />
            <rect x="1" y="7" width="14" height="2" />
            <rect x="1" y="12" width="14" height="2" />
        </svg>
    )
}

function useIsVisible(elementRef) {
    const [isVisible, setIsVisible] = useState(false)
    const OPTIONS = {
        root: null,
        rootMargin: "0px 0px 0px 0px",
        threshold: 0,
    }
    useEffect(() => {
        if (elementRef.current) {
            const observer = new IntersectionObserver((entries, observer) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setIsVisible(true)
                        observer.unobserve(elementRef.current)
                    }
                })
            }, OPTIONS)
            observer.observe(elementRef.current)
        }
    }, [elementRef])
    return isVisible
}